<template>
	<button
		class="c-SwitchToggle"
		:class="{ isActive: isActive }"
		type="button"
		:disabled="disabled"
		@mousedown.prevent
		@keyup.enter="handleKeyup"
		@keyup.space="handleKeyup">
		<input
			:id="switchToggleId"
			ref="checkboxElem"
			v-model="value"
			type="checkbox"
			class="c-SwitchToggle__input"
			:disabled="disabled"
			@change="toggleSwitch">
		<label
			class="c-SwitchToggle__label"
			:for="switchToggleId">
			<i></i>
			<span>{{ switchLabel }}</span>
		</label>
	</button>
</template>

<script>
export default {
	name: 'SwitchToggle',
	props: {
		/**
     * The SwitchToggle labels
     */
		labels: {
			type: Array,
			required: false,
			default: () => ['Off', 'On']
		},
		/**
     * The SwitchToggle data variable
		 * @values true, false
     */
		data: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the SwitchToggle is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the SwitchToggle is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			switchToggleId: this.randomizeId(),
			value: false,
			switchLabel: this.labels[0]
		}
	},
	watch: {
		data: {
			handler (newVal) {
				this.value = newVal
				this.updateSwitchLabel()
			},
			immediate: true
		}
	},
	methods: {
		randomizeId () {
			return `switchtoggleid${Math.floor(Math.random() * 100000000000000000000)}`
		},
		handleKeyup () {
			this.$refs.checkboxElem.click()
		},
		toggleSwitch () {
			this.updateSwitchLabel()
			/**
       * Triggers when SwitchToggle changes it's value
       * @event change
       * @property {boolean, string} value new value set
       */
			this.$emit('change', this.value)
		},
		updateSwitchLabel () {
			const [falseLabel, trueLabel] = this.labels
			if (this.value) {
				this.switchLabel = trueLabel
			} else {
				this.switchLabel = falseLabel
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-SwitchToggle {
  $label-font-size: 16px;

  height: 50px;
  border: none;
  outline: none;
  background-color: transparent;
  width: 100%;
  margin: 0;
  padding: 0;

  &::-moz-focus-inner {
    border: 0;
  }

  &:disabled .c-SwitchToggle__label {
    cursor: not-allowed;

    i {
      border-color: $un-n500 !important;
      background-color: $un-n100 !important;

      &::before {
        background-color: $un-n500 !important;
      }
    }
  }

  &:focus {
    span {
      color: $un-blue;
    }

    .c-SwitchToggle__input + .c-SwitchToggle__label {
      i {
        border-color: $un-blue;
        box-shadow: inset 0 0 0 1px $un-blue;
      }
    }

    .c-SwitchToggle__input:checked + .c-SwitchToggle__label {
      i {
        border-color: $un-n600;
        box-shadow: inset 0 0 0 1px $un-n600;
      }
    }
  }

  &.isActive {
    span {
      color: $un-blue;
    }

    .c-SwitchToggle__input + .c-SwitchToggle__label {
      i {
        border-color: $un-blue;
        box-shadow: inset 0 0 0 1px $un-blue;
      }
    }

    .c-SwitchToggle__input:checked + .c-SwitchToggle__label {
      i {
        border-color: $un-n600;
        box-shadow: inset 0 0 0 1px $un-n600;
      }
    }
  }

  &__input {
    width: 0;
    height: 0;
    visibility: hidden;
    opacity: 0;
    position: absolute;
  }

  &__label {
    display: flex;
    align-items: center;
    cursor: pointer;

    i {
      width: 36px;
      height: 20px;
      background-color: $un-n600;
      border: 1px solid $un-n600;
      border-radius: 10px;
      position: relative;
      margin-right: calc(#{$base-margin} / 2);
      transition: background-color $default-time $default-easing;

      &::before {
        content: '';
        position: absolute;
        top: 1px;
        left: 1px;
        width: 16px;
        height: 16px;
        border-radius: 16px;
        display: block;
        background-color: $un-white;
        transition: all $default-time $default-easing;
      }
    }

    span {
      color: $un-n900;
      font-size: 16px;
      font-family: $primary-font;
      letter-spacing: 0.5px;
      line-height: 24px;
    }
  }

  .c-SwitchToggle__input:checked + .c-SwitchToggle__label {
    i {
      border-color: $un-blue;
      background-color: $un-blue;

      &::before {
        transform: translateX(16px);
      }
    }
  }
}
</style>
